import { cn } from "@/lib/utils";
import type { CSSProperties, ReactNode } from "react";

/**
 * Shimmer gradient for placeholder shapes (document lines, body-well
 * backdrop, etc.). Pair with `animate-shimmer-text` to slide a highlight
 * band across the shape.
 */
export const FILE_RECORD_SKELETON_BG_STYLE: CSSProperties = {
  background:
    "linear-gradient(90deg, hsl(var(--muted-foreground) / 0.15) 0%, hsl(var(--muted-foreground) / 0.15) 35%, hsl(var(--muted-foreground) / 0.4) 50%, hsl(var(--muted-foreground) / 0.15) 65%, hsl(var(--muted-foreground) / 0.15) 100%)",
  backgroundSize: "200% 100%",
};

/**
 * Mirror of `FILE_RECORD_SKELETON_BG_STYLE` for text: gradient clipped to
 * glyph shapes via `background-clip: text`. Same stop positions so the
 * highlight sweeps in phase with body shapes.
 */
export const FILE_RECORD_SKELETON_TEXT_STYLE: CSSProperties = {
  background:
    "linear-gradient(90deg, hsl(var(--muted-foreground)) 0%, hsl(var(--muted-foreground)) 35%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground)) 65%, hsl(var(--muted-foreground)) 100%)",
  backgroundSize: "200% 100%",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  color: "transparent",
};

function formatLabel(label: string | undefined): string {
  const raw = label?.trim() || "Working";
  const capitalized = raw.charAt(0).toUpperCase() + raw.slice(1);
  if (/[.!?…]\s*$/.test(capitalized)) return capitalized;
  return capitalized + "...";
}

function SkeletonContentLines() {
  const lineWidths = ["100%", "92%", "85%", "96%", "78%"];
  return (
    <div className="w-full h-full px-4 py-3.5 flex flex-col gap-2 justify-center">
      <div
        className="h-2.5 w-3/5 rounded-sm animate-shimmer-text mb-1"
        style={FILE_RECORD_SKELETON_BG_STYLE}
      />
      {lineWidths.map((w, i) => (
        <div
          key={i}
          className="h-2 rounded-sm animate-shimmer-text"
          style={{ width: w, ...FILE_RECORD_SKELETON_BG_STYLE }}
        />
      ))}
    </div>
  );
}

export type FileRecordSkeletonKind = "image" | "video" | "file_record";

function resolveDefaultHeight(
  width: number,
  kind: FileRecordSkeletonKind | undefined,
): number {
  switch (kind) {
    case "image":
      return Math.round(width * (768 / 1408));
    case "video":
      return Math.round(width * (9 / 16));
    case "file_record":
    default:
      // Matches DEFAULT_FILE_NODE_HEIGHT (a multiple of 50, grid-aligned center)
      // so the node doesn't jump height when the skeleton swaps to the card.
      return 200;
  }
}

export interface FileRecordCardSkeletonProps {
  width: number;
  height?: number;
  kind?: FileRecordSkeletonKind;
  label?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Shimmer loading skeleton for file record canvas nodes. Mirrors the
 * embedded `FileRecordCard` layout exactly so the skeleton → real card
 * swap has zero visible reflow. Used for all loading states: agent
 * placement, file upload in-flight, workspace copy, and backend
 * processing (pending/processing status with no URL yet).
 */
export function FileRecordCardSkeleton({
  width,
  height,
  kind,
  label,
  children,
  className,
}: FileRecordCardSkeletonProps) {
  const resolvedHeight = height ?? resolveDefaultHeight(width, kind);
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label || "Loading"}
      className={cn(
        "relative flex flex-col overflow-hidden rounded-xl bg-card opacity-75",
        className,
      )}
      style={{ width, height: resolvedHeight, containerType: "inline-size" }}
    >
      <div className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden">
        {children ?? <SkeletonContentLines />}
      </div>
      <div className="flex items-center gap-2 shrink-0 px-2.5 py-2">
        <p
          className="flex-1 min-w-0 text-[12px] font-semibold leading-tight truncate animate-shimmer-text"
          style={FILE_RECORD_SKELETON_TEXT_STYLE}
        >
          {formatLabel(label)}
        </p>
      </div>
    </div>
  );
}

export default FileRecordCardSkeleton;
